import { Component, HostListener, OnInit, signal, effect, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ConfirmationModalService,
  ActiveConfirmationModal,
  ConfirmationModalButton,
} from "../../core/services/confirmation-modal.service";

@Component({
  selector: "app-confirmation-modal",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./confirmation-modal.component.html",
  styleUrls: ["./confirmation-modal.component.scss"],
})
export class ConfirmationModalComponent implements OnInit {
  private confirmationModalService = inject(ConfirmationModalService);

  public modals = this.confirmationModalService.activeModals;
  public animatingOut = signal<Set<string>>(new Set());

  ngOnInit() {
    // Focus trap effect
    effect(() => {
      const modalList = this.modals();
      if (modalList.length > 0) {
        setTimeout(() => {
          this.trapFocus();
        }, 100);
      }
    });
  }

  @HostListener("document:keydown.escape")
  onEscapeKey() {
    const modalList = this.modals();
    if (modalList.length === 0) return;

    const topModal = modalList[modalList.length - 1];
    if (topModal.closeOnEscape !== false) {
      this.closeModal(topModal);
    }
  }

  onBackdropClick(event: MouseEvent, modal: ActiveConfirmationModal) {
    if (event.target === event.currentTarget && modal.closeOnBackdropClick !== false) {
      this.closeModal(modal);
    }
  }

  closeModal(modal: ActiveConfirmationModal, result?: unknown) {
    // Trigger animation
    this.animatingOut.update((set) => {
      const newSet = new Set(set);
      newSet.add(modal.id);
      return newSet;
    });

    // Wait for animation to complete
    setTimeout(() => {
      this.confirmationModalService.closeById(modal.id, result);
      this.animatingOut.update((set) => {
        const newSet = new Set(set);
        newSet.delete(modal.id);
        return newSet;
      });
    }, 200);
  }

  dismissModal(modal: ActiveConfirmationModal) {
    this.animatingOut.update((set) => {
      const newSet = new Set(set);
      newSet.add(modal.id);
      return newSet;
    });

    setTimeout(() => {
      this.confirmationModalService.dismiss();
      this.animatingOut.update((set) => {
        const newSet = new Set(set);
        newSet.delete(modal.id);
        return newSet;
      });
    }, 200);
  }

  async onButtonClick(modal: ActiveConfirmationModal, button: ConfirmationModalButton) {
    if (button.loading) return;

    try {
      await button.action();
    } catch (error) {
      console.error("Erro ao executar ação do botão:", error);
    }
  }

  getIconSvg(icon?: string): string {
    switch (icon) {
      case "success":
        return `<svg class="confirmation-modal-icon confirmation-modal-icon--success" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fill="currentColor"/></svg>`;
      case "error":
        return `<svg class="confirmation-modal-icon confirmation-modal-icon--error" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="currentColor"/></svg>`;
      case "warning":
        return `<svg class="confirmation-modal-icon confirmation-modal-icon--warning" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" fill="currentColor"/></svg>`;
      case "info":
        return `<svg class="confirmation-modal-icon confirmation-modal-icon--info" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" fill="currentColor"/></svg>`;
      case "question":
        return `<svg class="confirmation-modal-icon confirmation-modal-icon--question" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" fill="currentColor"/></svg>`;
      default:
        return "";
    }
  }

  getWidthClass(width?: string): string {
    switch (width) {
      case "sm":
        return "max-w-sm";
      case "md":
        return "max-w-md";
      case "lg":
        return "max-w-lg";
      case "xl":
        return "max-w-xl";
      case "full":
        return "max-w-full";
      default:
        return "max-w-md";
    }
  }

  getButtonClass(variant?: string): string {
    switch (variant) {
      case "primary":
        return "btn-primary";
      case "secondary":
        return "btn-secondary";
      case "danger":
        return "btn-danger";
      case "ghost":
        return "btn-ghost";
      default:
        return "btn-secondary";
    }
  }

  private trapFocus() {
    const modalElements = document.querySelectorAll('[role="dialog"]');
    if (modalElements.length === 0) return;

    const lastModal = modalElements[modalElements.length - 1];
    const focusableElements = lastModal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      const firstElement = focusableElements[0] as HTMLElement;
      firstElement.focus();
    }
  }

  isAnimatingOut(modalId: string): boolean {
    return this.animatingOut().has(modalId);
  }

  trackModal(index: number, modal: ActiveConfirmationModal): string {
    return modal.id;
  }

  trackButton(index: number): number {
    return index;
  }
}
